// Generic TLDs recognised on bare domains (no protocol), plus the legal ones firms actually use.
// Explicit http(s):// URLs and email addresses aren't limited to this list.
const GENERIC_TLDS = new Set([
    'aero',
    'app',
    'asia',
    'attorney',
    'biz',
    'com',
    'coop',
    'dev',
    'edu',
    'gov',
    'info',
    'law',
    'lawyer',
    'legal',
    'museum',
    'name',
    'net',
    'org',
    'pro',
    'shop',
    'web',
    'рф',
]);

// Country-code TLDs. A real list rather than any two letters, so `node.js` and `file.md` stay plain text.
const COUNTRY_TLD_REGEX =
    /^(?:a[cdefgilmnoqrstuwxz]|b[abdefghijmnorstvwyz]|c[acdfghiklmnoruvwxyz]|d[ejkmoz]|e[cegrstu]|f[ijkmor]|g[abdefghilmnpqrstuwy]|h[kmnrtu]|i[delmnoqrst]|j[emop]|k[eghimnprwyz]|l[abcikrstuvy]|m[acdeghklmnopqrstuvwxyz]|n[acefgilopruz]|om|p[aefghklmnrstwy]|qa|r[eosuw]|s[abcdeghijklmnortuvxyz]|t[cdfghjklmnortvwz]|u[agksyz]|v[aceginu]|w[fs]|y[et]|z[amw])$/;

// Every pattern is anchored and applied to a single whitespace-free word, so matching stays
// linear in the input length (linkify-it's unanchored scan went quadratic: GHSA-v245-v573-v5vm).
const WORD_SEPARATOR_REGEX = /([\s<>"]+)/;
const SCHEME_URL_REGEX = /^https?:\/\/[^/?#:]+/i;
const MAILTO_PREFIX_REGEX = /^mailto:/i;
const EMAIL_REGEX = /^[\p{L}\p{N}._%+-]+@([^@]+)$/u;
const BARE_URL_REGEX = /^([^:/?#]+)(?::\d{1,5})?(?:[/?#].*)?$/u;
const DOMAIN_LABEL_REGEX = /^[\p{L}\p{N}-]+$/u;
const LETTERS_REGEX = /^\p{L}{2,}$/u;
const LABEL_PREFIX_REGEX = /^([\p{L}\p{N}]+:)(.+)$/u;

const LEADING_PUNCTUATION = new Set(['(', '[', '{', "'"]);
const TRAILING_PUNCTUATION = new Set(['.', ',', ';', ':', '!', '?', "'", ')', ']', '}']);
const CLOSING_BRACKETS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

interface Link {
    href: string;
    isEmail: boolean;
}

function isDomain(host: string, allowAnyTld: boolean): boolean {
    const labels = host.split('.');
    if (labels.length < 2) {
        return false;
    }

    const labelsValid = labels.every(
        (label) => DOMAIN_LABEL_REGEX.test(label) && !label.startsWith('-') && !label.endsWith('-'),
    );
    if (!labelsValid) {
        return false;
    }

    const tld = labels[labels.length - 1].toLowerCase();
    if (allowAnyTld) {
        return LETTERS_REGEX.test(tld) || tld.startsWith('xn--');
    }
    return GENERIC_TLDS.has(tld) || COUNTRY_TLD_REGEX.test(tld) || tld.startsWith('xn--');
}

function isEmail(text: string): boolean {
    const match = EMAIL_REGEX.exec(text);
    return !!match && isDomain(match[1], true);
}

function toLink(text: string): Link | null {
    if (SCHEME_URL_REGEX.test(text)) {
        return { href: text, isEmail: false };
    }

    if (MAILTO_PREFIX_REGEX.test(text)) {
        return isEmail(text.slice('mailto:'.length)) ? { href: text, isEmail: true } : null;
    }

    if (isEmail(text)) {
        return { href: `mailto:${text}`, isEmail: true };
    }

    const bareUrl = BARE_URL_REGEX.exec(text);
    if (bareUrl && isDomain(bareUrl[1], false)) {
        return { href: `https://${text}`, isEmail: false };
    }

    return null;
}

function toAnchor(text: string, link: Link): string {
    if (link.isEmail) {
        return `<a href="${link.href}">${text}</a>`;
    }
    return `<a href="${link.href}" target="_blank">${text}</a>`;
}

/**
 * Splits surrounding punctuation off a word, e.g. `(example.com).` -> `(`, `example.com`, `).`.
 * Closing brackets stay in the core when they balance an opening one inside it, as in `/a_(b)`.
 */
function splitPunctuation(word: string): [string, string, string] {
    let start = 0;
    while (start < word.length && LEADING_PUNCTUATION.has(word[start])) {
        start++;
    }

    let end = word.length;
    const openCounts: Record<string, number> = { '(': 0, '[': 0, '{': 0 };
    const closeCounts: Record<string, number> = { ')': 0, ']': 0, '}': 0 };
    for (let i = start; i < end; i++) {
        const char = word[i];
        if (char in openCounts) openCounts[char]++;
        if (char in closeCounts) closeCounts[char]++;
    }

    while (end > start && TRAILING_PUNCTUATION.has(word[end - 1])) {
        const char = word[end - 1];
        const opening = CLOSING_BRACKETS[char];
        if (opening && openCounts[opening] >= closeCounts[char]) {
            break;
        }
        if (opening) closeCounts[char]--;
        end--;
    }

    return [word.slice(0, start), word.slice(start, end), word.slice(end)];
}

function linkifyWord(word: string): string {
    const [leading, core, trailing] = splitPunctuation(word);
    if (!core) {
        return word;
    }

    const link = toLink(core);
    if (link) {
        return leading + toAnchor(core, link) + trailing;
    }

    // `Email:help@example.com` / `Web:example.com`: keep the label as text and link the rest.
    const labelled = LABEL_PREFIX_REGEX.exec(core);
    const labelledLink = labelled ? toLink(labelled[2]) : null;
    if (labelled && labelledLink) {
        return leading + labelled[1] + toAnchor(labelled[2], labelledLink) + trailing;
    }

    return word;
}

function linkifySegment(text: string): string {
    return text
        .split(WORD_SEPARATOR_REGEX)
        .map((part, index) => (index % 2 === 1 ? part : linkifyWord(part)))
        .join('');
}

/**
 * Linkifies the text around existing `<a ...>...</a>` elements, keeping those as-is.
 * A forward scan rather than one split regex, which went quadratic on many unclosed `<a` tags.
 */
function linkifyOutsideAnchors(text: string): string {
    const anchorStartRegex = /<a(?!\w)/gi;
    const anchorEndRegex = /<\/a>/gi;
    let result = '';
    let position = 0;

    while (position < text.length) {
        anchorStartRegex.lastIndex = position;
        const start = anchorStartRegex.exec(text);
        const openTagEnd = start ? text.indexOf('>', start.index) : -1;
        if (!start || openTagEnd === -1) {
            break;
        }

        anchorEndRegex.lastIndex = openTagEnd + 1;
        const end = anchorEndRegex.exec(text);
        if (!end) {
            break;
        }

        const anchorEnd = end.index + end[0].length;
        result += linkifySegment(text.slice(position, start.index));
        result += text.slice(start.index, anchorEnd);
        position = anchorEnd;
    }

    return result + linkifySegment(text.slice(position));
}

export function getEmailSignatureHyperlink(emailSignature?: string | null): string {
    const normalized = emailSignature?.replaceAll('\n', '<br>') ?? '';
    if (!normalized) {
        return '';
    }

    return linkifyOutsideAnchors(normalized);
}
